import * as React from 'react';
import styled from 'styled-components';

import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';

import { TABLE_COLUMNS } from 'modules/workflowIndex/constants';
import { HeaderButton } from 'modules/templateIndex/templateIndexCommon/TableView.styles';
import { DoubleArrow, DownArrow, UpArrow } from 'ui/icons';
import { colors } from 'ui/theme';

const { useCallback } = React;

type Column = {
  id: TABLE_COLUMNS;
  label: string;
  isSortable: boolean;
  initialSortDirection?: SORT_DIRECTIONS;
};

type HeaderRowCellProps = {
  column: Column;
  isActiveSortColumn: boolean;
  sortDirection: SORT_DIRECTIONS;
  setSortedBy: (sortedBy: TABLE_COLUMNS) => void;
  setSortDirection: (sortDirection: SORT_DIRECTIONS) => void;
};

const StyledHeaderButton = styled(HeaderButton)`
  text-align: left;
  color: ${(props: { isSortable: boolean; isActiveSortColumn: boolean }) => {
    if (!props.isSortable) {
      return colors.textSecondary;
    }

    return props.isActiveSortColumn ? colors.brandPurple : colors.textPrimary;
  }};
  display: flex;
  align-items: center;
`;

export const HeaderRowCell = ({
  column,
  isActiveSortColumn,
  sortDirection,
  setSortDirection,
  setSortedBy,
}: HeaderRowCellProps) => {
  const isAscendingSort = sortDirection === SORT_DIRECTIONS.ASCENDING;
  const handleClick = useCallback(() => {
    if (isActiveSortColumn) {
      setSortDirection(isAscendingSort ? SORT_DIRECTIONS.DESCENDING : SORT_DIRECTIONS.ASCENDING);
    } else if (column.isSortable) {
      setSortedBy(column.id);
      setSortDirection(column.initialSortDirection || SORT_DIRECTIONS.DESCENDING);
    }
  }, [
    setSortDirection,
    setSortedBy,
    column.isSortable,
    column.id,
    isAscendingSort,
    isActiveSortColumn,
    column.initialSortDirection,
  ]);

  return (
    <StyledHeaderButton
      isSortable={column.isSortable}
      isActiveSortColumn={isActiveSortColumn}
      as={column.isSortable ? 'button' : 'div'}
      data-test={`column-header-${column.id}`}
      key={column.id}
      onClick={handleClick}
    >
      <span>{column.label}</span>
      {column.isSortable && !isActiveSortColumn && <DoubleArrow isColorInherited />}
      {column.isSortable && isActiveSortColumn && isAscendingSort && <UpArrow isColorInherited />}
      {column.isSortable && isActiveSortColumn && !isAscendingSort && (
        <DownArrow isColorInherited />
      )}
    </StyledHeaderButton>
  );
};
